import { useQuery } from '@tanstack/react-query';
import {
  fetchPaymentTypes,
  fetchProjectTypes,
  fetchTaskTypes,
  type PaymentType,
  type ProjectType,
  type TaskType,
} from '../services/api/templateTypes';

/**
 * Phase 41 Plan 41-10 (Surface 1) — TanStack Query hooks for the template-type trio.
 *
 * Three independently keyed queries (one per template set) so cache eviction
 * on one kind doesn't ripple to the others. `staleTime` is set to 5 minutes
 * because admin template configuration is essentially static at the timescale
 * of a project create / edit interaction — repeated picker opens shouldn't
 * refetch.
 *
 * NAMING — see `services/api/templateTypes.ts` header. The legacy
 * `useProjectTemplates` hook already owns the Phase 19 template-with-states
 * concept; this module owns the Phase 41 / v7.0 template-type trio.
 */

export function usePaymentTypes() {
  return useQuery({
    queryKey: ['paymentTypes'],
    queryFn: fetchPaymentTypes,
    staleTime: 5 * 60_000,
  });
}

export function useProjectTypes() {
  return useQuery({
    queryKey: ['projectTypes'],
    queryFn: fetchProjectTypes,
    staleTime: 5 * 60_000,
  });
}

export function useTaskTypes() {
  return useQuery({
    queryKey: ['taskTypes'],
    queryFn: fetchTaskTypes,
    staleTime: 5 * 60_000,
  });
}

// Re-export the row interfaces so consumers don't have to dual-import from
// the API module (mirrors the useProjectTemplates re-export pattern).
export type { PaymentType, ProjectType, TaskType };
